import { signal } from '@preact/signals';
import en from './en';
import de from './de';
import fr from './fr';
import es from './es';
import type { Dictionary } from './en';

export type Locale = 'en' | 'de' | 'fr' | 'es';
export const SUPPORTED_LOCALES: Locale[] = ['en', 'de', 'fr', 'es'];

const dictionaries: Record<Locale, Dictionary> = { en, de, fr, es };

const STORAGE_KEY = 'as_locale';

function isSupported(value: string): value is Locale {
  return (SUPPORTED_LOCALES as string[]).includes(value);
}

function detectInitialLocale(): Locale {
  // 1. URL ?lang= wins — useful for translator previews + sharing screenshots
  //    in a specific locale without touching localStorage.
  if (typeof window !== 'undefined' && window.location?.search) {
    try {
      const param = new URLSearchParams(window.location.search).get('lang')?.toLowerCase();
      if (param && isSupported(param)) return param;
    } catch {
      // ignore malformed URLs
    }
  }
  // 2. Persisted manual override from the LanguageToggle.
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isSupported(stored)) return stored;
  } catch {
    // localStorage unavailable
  }
  // 3. Browser language; fall back to EN.
  if (typeof navigator !== 'undefined') {
    const browser = navigator.language.slice(0, 2).toLowerCase();
    if (isSupported(browser)) return browser;
  }
  return 'en';
}

export const locale = signal<Locale>(detectInitialLocale());

export function setLocale(next: Locale): void {
  locale.value = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // ignore
  }
}

type Vars = Record<string, string | number>;

function interpolate(template: string, vars: Vars | undefined): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = vars[key];
    return value === undefined ? `{${key}}` : String(value);
  });
}

type PathOf<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends object
    ? PathOf<T[K], `${P}${K}.`>
    : `${P}${K}`;
}[keyof T & string];

export type TranslationKey = PathOf<Dictionary>;

function walk(dict: Dictionary, path: string): string {
  const segments = path.split('.');
  let current: unknown = dict;
  for (const segment of segments) {
    if (current && typeof current === 'object' && segment in current) {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return path;
    }
  }
  return typeof current === 'string' ? current : path;
}

export function t(key: TranslationKey, vars?: Vars): string {
  const dict = dictionaries[locale.value];
  return interpolate(walk(dict, key), vars);
}
